import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Form, Formik } from "formik";
import Stack from "@mui/material/Stack";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { useMutation } from "react-query";
import { CognitoUserPoolType, UserAccountsService } from "gen/clients";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import DialogActions from "@mui/material/DialogActions";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { normalizePhoneNumberForSubmission, validatePhoneNumber } from "utils/stringUtils";

interface Props {
  username: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  onSuccess: () => void;
  onClose: () => void;
}

enum FieldName {
  firstName = "firstName",
  lastName = "lastName",
  email = "email",
  phoneNumber = "phoneNumber"
}

interface FormValues {
  [FieldName.firstName]: string;
  [FieldName.lastName]: string;
  [FieldName.email]: string;
  [FieldName.phoneNumber]: string;
}

const EditAttributesDialog: React.FC<Props> = ({
  username,
  firstName,
  lastName,
  email,
  phoneNumber,
  onSuccess,
  onClose
}) => {
  const { mutateAsync, error } = useMutation(UserAccountsService.updateCognitoUserAttributes, { onSuccess });

  const initialValues = React.useMemo<FormValues>(
    () => ({
      [FieldName.firstName]: firstName || "",
      [FieldName.lastName]: lastName || "",
      [FieldName.email]: email || "",
      [FieldName.phoneNumber]: phoneNumber || ""
    }),
    [firstName, lastName, email, phoneNumber]
  );

  const validate = React.useCallback((values: FormValues) => {
    const phoneNumberError = validatePhoneNumber(values[FieldName.phoneNumber]);

    return phoneNumberError
      ? {
          [FieldName.phoneNumber]: phoneNumberError
        }
      : {};
  }, []);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutateAsync({
        userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
        cognitoUsername: encodeURIComponent(username),
        requestBody: {
          firstName: values[FieldName.firstName],
          lastName: values[FieldName.lastName],
          email: values[FieldName.email],
          phoneNumber: normalizePhoneNumberForSubmission(values[FieldName.phoneNumber])
        }
      });
    },
    [mutateAsync, username]
  );

  return (
    <>
      {error && <SnackbarApiError error={error} />}
      <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
        <DialogTitle>Edit Attributes</DialogTitle>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnMount={false}>
          {({ isSubmitting }) => (
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <DialogContent>
                <Stack spacing={3}>
                  <TextInputField name={FieldName.firstName} required={true} label="First Name" />
                  <TextInputField name={FieldName.lastName} required={true} label="Last Name" />
                  <TextInputField name={FieldName.email} required={true} label="Email" />
                  <TextInputField
                    name={FieldName.phoneNumber}
                    required={true}
                    label="Phone Number"
                    helperText="The phone number must include a country code. Example: +1 555 555 5555."
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <LoadingButton type="submit" color="primary" variant="contained" isLoading={isSubmitting}>
                  Save
                </LoadingButton>
                <Button color="secondary" onClick={onClose}>
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export { EditAttributesDialog };
